import React, { useEffect, useState } from 'react'
import { FaTimes as X } from 'react-icons/fa'
import {
  useAudioDevicesStore,
  refreshAudioDevices,
  probeInputChannelCount,
  DEFAULT_OUTPUT_ID,
  DEFAULT_INPUT_ID,
  type AudioDeviceInfo,
} from '@/stores/audioDevicesStore'
import {
  useSequencerStore,
  selectCurrentBuses,
  selectCurrentContainer,
} from '@/stores/sequencerStore'
import { BusRack } from '@/components/werkshop/BusRack'
import { InputMeter } from '@/components/werkshop/InputMeter'
import { getAudioInput } from '@/audio/AudioInput'
import { getMultiInput } from '@/audio/MultiInput'
import { MONITOR_METER_ID } from '@/audio/meters'
import { useUIStore, type CountInBars } from '@/stores/uiStore'
import {
  canCaptureAudioInput,
  audioInputUnavailableReason,
} from '@/audio/capabilities'
import { useAudioEngine } from '@/hooks/useAudioEngine'
import {
  useAudioRecording,
  type AudioRecordingResult,
} from '@/hooks/useAudioRecording'

interface AudioPanelProps {
  onClose: () => void
}

/**
 * In-page audio I/O surface — companion to `SequencerMidiPanel`
 * which handles controller routing. This phase (2a) ships only the
 * output device picker; per-bus effects, input monitoring, and
 * recording arrive in later phases.
 *
 * The picker is disabled when the browser can't route Web Audio to
 * a non-default sink (Firefox without a flag, iPad/Safari via
 * Capacitor's WKWebView). In that case we render the hardware
 * list but grey it out and explain that playback will keep using
 * the system default — no misleading affordance.
 */
export const AudioPanel: React.FC<AudioPanelProps> = ({ onClose }) => {
  const supported = useAudioDevicesStore(s => s.supported)
  const canSwitchOutput = useAudioDevicesStore(s => s.canSwitchOutput)
  const outputScope = useAudioDevicesStore(s => s.outputScope)
  const outputs = useAudioDevicesStore(s => s.outputs)
  const selectedOutputId = useAudioDevicesStore(s => s.selectedOutputId)
  const setSelectedOutputId = useAudioDevicesStore(s => s.setSelectedOutputId)

  // Re-enumerate on mount so the picker reflects any hotplug since
  // the engine last cached the device list. Cheap call; no permission
  // prompt. Additional refreshes happen on `devicechange` events
  // wired inside the audio engine's `ensureOutputSink`.
  useEffect(() => {
    void refreshAudioDevices()
  }, [])

  return (
    <section
      className="rounded-xl border border-brd-subtle bg-surface-sunken overflow-hidden flex flex-col max-h-[80vh]"
      aria-label="Audio I/O settings"
    >
      <header className="shrink-0 flex items-center justify-between px-4 py-2 bg-surface-raised border-b border-brd-subtle">
        <div>
          <h3 className="text-sm font-semibold text-content-primary">Audio</h3>
          <p className="text-[11px] text-content-tertiary">
            Route engine output to a specific interface. Bus mixing, input
            monitoring, and recording arrive in later phases.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-md text-content-secondary hover:text-content-primary hover:bg-surface-sunken transition-colors"
          aria-label="Close Audio panel"
        >
          <X size={14} />
        </button>
      </header>

      <div className="p-4 flex flex-col gap-3 overflow-y-auto min-h-0">
        <section className="bg-surface-raised border border-brd-subtle rounded-xl p-3 flex flex-col gap-2">
          <div className="flex items-baseline justify-between">
            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">
              Output device
            </h4>
            {!canSwitchOutput && (
              <span
                className="text-[10px] font-mono text-content-tertiary italic"
                title="This browser / runtime doesn't expose audio output routing. All sound goes to the OS-level default."
              >
                system default only
              </span>
            )}
            {canSwitchOutput && outputScope === 'system' && (
              <span
                className="text-[10px] font-mono text-content-tertiary italic"
                title="Selecting an output here changes the macOS system-default device. Other apps follow the same change."
              >
                system-wide
              </span>
            )}
          </div>

          {!supported ? (
            <>
              <p className="text-[11px] text-content-tertiary italic">
                Device enumeration isn&apos;t available in this runtime. On iPad
                / Mac the app needs microphone permission declared (Info.plist +
                entitlements) before Web Audio exposes the device list — a newer
                app build should unlock it.
              </p>
              <RuntimeDiagnostics />
            </>
          ) : (
            <label className="flex flex-col gap-1">
              <select
                value={selectedOutputId}
                onChange={e => setSelectedOutputId(e.target.value)}
                disabled={!canSwitchOutput}
                className="w-full rounded-md border border-brd-subtle bg-surface-sunken px-2 py-1.5 text-[12px] font-mono text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)] disabled:opacity-50"
              >
                <option value={DEFAULT_OUTPUT_ID}>System default</option>
                {outputs
                  .filter(d => d.id && d.id !== DEFAULT_OUTPUT_ID)
                  .map(d => (
                    <option key={d.id} value={d.id}>
                      {formatDeviceOption(d, 'out')}
                    </option>
                  ))}
              </select>
              <span className="text-[10px] text-content-tertiary">
                {canSwitchOutput && outputScope === 'system'
                  ? 'Selecting a device changes the macOS system default — other apps follow the same change.'
                  : canSwitchOutput
                    ? 'Change takes effect immediately — no reload needed.'
                    : 'Output routing on this runtime goes to the system default.'}
              </span>
            </label>
          )}

          {outputs.length === 0 && supported && (
            <p className="text-[10px] text-content-tertiary italic">
              No output devices detected yet. Labels may appear blank until the
              page is granted microphone permission once (browser privacy
              quirk).
            </p>
          )}
        </section>

        <AudioInputSection />

        <InputSourcesSection />

        <BusRack />
      </div>
    </section>
  )
}

/**
 * P5c — list of per-channel input routings. Each row binds one
 * hardware channel on a single device to a bus. Multiple rows can
 * share a device + different channels (typical multi-channel
 * interface use). Gated behind the same `Monitor ON` toggle as the
 * legacy default-mic path so the user never has to juggle two
 * "enable inputs" buttons.
 *
 * Channel counts on web are only known after a stream opens. The
 * MultiInputRouter reports them back via `onCaptureChange`; until
 * then the channel picker shows the stored index and a soft
 * "probe" hint to explain why options might be sparse.
 */
const InputSourcesSection: React.FC = () => {
  const container = useSequencerStore(selectCurrentContainer)
  const sources = container?.inputSources ?? []
  const buses = useSequencerStore(selectCurrentBuses)
  const inputs = useAudioDevicesStore(s => s.inputs)
  const monitor = useAudioDevicesStore(s => s.inputMonitorEnabled)
  const addInputSource = useSequencerStore(s => s.addInputSource)
  const removeInputSource = useSequencerStore(s => s.removeInputSource)
  const updateInputSource = useSequencerStore(s => s.updateInputSource)
  const [errors, setErrors] = useState<Record<string, string>>({})
  // Live channel counts the router has observed (web-only; native
  // plugin reports them via audioDevicesStore.inputs). Seeded on
  // mount from whatever the router already has.
  const [liveCounts, setLiveCounts] = useState<Record<string, number>>({})

  useEffect(() => {
    const router = getMultiInput()
    if (!router) return
    return router.subscribe({
      onError: (sourceId, message) => {
        setErrors(prev => ({ ...prev, [sourceId]: message }))
      },
      onCaptureChange: (deviceId, count) => {
        setLiveCounts(prev => ({ ...prev, [deviceId]: count }))
      },
    })
  }, [monitor])

  const captureSupported = canCaptureAudioInput()
  const captureReason = audioInputUnavailableReason()

  return (
    <section className="bg-surface-raised border border-brd-subtle rounded-xl p-3 flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">
            Input sources
          </h4>
          <p className="text-[10px] text-content-tertiary mt-0.5">
            Route individual hardware channels to mixer strips. Added sources
            open their stream when Monitor is ON.
          </p>
        </div>
        <button
          type="button"
          onClick={() => addInputSource({ busId: buses[0]?.id })}
          className="text-[10px] font-mono rounded-md border border-brd-subtle px-2 py-1 text-content-secondary hover:text-accent hover:border-[color:var(--accent-halo)] transition-colors"
          title="Add a new input → mixer routing"
        >
          + Add source
        </button>
      </div>
      {!captureSupported && captureReason && (
        <p className="text-[10px] text-content-tertiary bg-surface-sunken border border-brd-subtle rounded px-2 py-1">
          {captureReason}
        </p>
      )}

      {sources.length === 0 ? (
        <p className="text-[10px] text-content-tertiary italic">
          No sources yet. Add one to route a single channel (e.g. ch 3 on a
          multi-in interface) to a specific bus, bypassing the shared monitor
          stream.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {sources.map(src => {
            const device = inputs.find(i => i.id === src.deviceId)
            const nativeCount = device?.channelCount ?? 0
            const liveCount = liveCounts[src.deviceId] ?? 0
            // Prefer the live-stream count when the router has opened
            // the device — it's the only truth we have on web. Fall
            // back to the native plugin's count (Catalyst / iPad), then
            // to 2 as a last resort so the picker always has options.
            const channelCount = Math.max(nativeCount, liveCount, 2)
            const mode: 1 | 2 = src.channelCount === 2 ? 2 : 1
            // Mono: one option per channel. Stereo: one option per
            // consecutive pair (1-2, 3-4, …). Clamp the last stereo
            // option so we never point past the end of the device.
            const channelOptions =
              mode === 2
                ? Array.from(
                    { length: Math.max(1, Math.floor(channelCount / 2)) },
                    (_, i) => i * 2,
                  )
                : Array.from({ length: channelCount }, (_, i) => i)
            const channelLabel = (i: number): string => {
              if (mode === 2) {
                const leftName = device?.channelNames?.[i]
                const rightName = device?.channelNames?.[i + 1]
                if (leftName && rightName) {
                  return `${i + 1}-${i + 2} — ${leftName} / ${rightName}`
                }
                return `Ch ${i + 1}-${i + 2} (stereo)`
              }
              const name = device?.channelNames?.[i]
              return name && name.trim() !== ''
                ? `${i + 1} — ${name}`
                : `Ch ${i + 1}`
            }
            const error = errors[src.id]
            return (
              <div
                key={src.id}
                className="grid grid-cols-[1fr_auto_auto_1fr_auto] gap-2 items-center"
              >
                <select
                  value={src.deviceId}
                  onChange={e => {
                    const nextId = e.target.value
                    updateInputSource(src.id, { deviceId: nextId })
                    setErrors(prev => {
                      const { [src.id]: _discard, ...rest } = prev
                      return rest
                    })
                    // Kick off a background probe so the channel
                    // dropdown populates without requiring Monitor ON.
                    // Safe to call on native (it no-ops there).
                    void probeInputChannelCount(nextId)
                  }}
                  className="rounded-md border border-brd-subtle bg-surface-sunken px-2 py-1 text-[11px] font-mono text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
                >
                  <option value="">System default input</option>
                  {inputs
                    .filter(d => d.id && d.id !== DEFAULT_INPUT_ID)
                    .map(d => (
                      <option key={d.id} value={d.id}>
                        {formatDeviceOption(d, 'in')}
                      </option>
                    ))}
                </select>
                <select
                  value={mode}
                  onChange={e => {
                    const nextMode = Number(e.target.value) as 1 | 2
                    // When switching to stereo, snap channelIndex to
                    // an even number so the pair is aligned; the
                    // router clamps anyway but UX is cleaner this way.
                    const nextIndex =
                      nextMode === 2
                        ? src.channelIndex - (src.channelIndex % 2)
                        : src.channelIndex
                    updateInputSource(src.id, {
                      channelCount: nextMode,
                      channelIndex: nextIndex,
                    })
                  }}
                  className="rounded-md border border-brd-subtle bg-surface-sunken px-2 py-1 text-[11px] font-mono text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
                  title="Mono = single channel fed through the bus panner. Stereo = consecutive L/R pair preserved as a true stereo source."
                  disabled={channelCount < 2}
                >
                  <option value={1}>Mono</option>
                  <option value={2}>Stereo</option>
                </select>
                <select
                  value={src.channelIndex}
                  onChange={e =>
                    updateInputSource(src.id, {
                      channelIndex: Number(e.target.value),
                    })
                  }
                  className="rounded-md border border-brd-subtle bg-surface-sunken px-2 py-1 text-[11px] font-mono text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
                  title="Channel on the selected device. Stereo pairs consume two consecutive channels; change the mode to Mono to pick individual ones."
                >
                  {channelOptions.map(i => (
                    <option key={i} value={i}>
                      {channelLabel(i)}
                    </option>
                  ))}
                </select>
                <select
                  value={src.busId}
                  onChange={e =>
                    updateInputSource(src.id, { busId: e.target.value })
                  }
                  className="rounded-md border border-brd-subtle bg-surface-sunken px-2 py-1 text-[11px] font-mono text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
                >
                  {buses.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => removeInputSource(src.id)}
                  className="text-[10px] font-mono text-content-tertiary hover:text-destructive transition-colors"
                  title={`Remove this routing`}
                >
                  ✕
                </button>
                {/* Live peak meter spans the row so the user can see
                    real signal level on each source at a glance.
                    Only polls while monitoring is on — the RAF loop
                    resets to floor on disable. */}
                <div className="col-span-5">
                  <InputMeter meterId={src.id} enabled={monitor} />
                </div>
                {error && (
                  <p
                    className="col-span-5 text-[10px] font-mono text-destructive"
                    role="alert"
                  >
                    {error}
                  </p>
                )}
              </div>
            )
          })}
          {!monitor && (
            <p className="text-[10px] text-content-tertiary italic">
              Monitor is off — sources are saved but inactive. Turn Monitor ON
              to open the streams.
            </p>
          )}
        </div>
      )}
    </section>
  )
}

/**
 * Live-input routing. Shows a device picker, the bus the input
 * routes to, and a monitor toggle. Flipping monitor ON triggers the
 * browser permission prompt via AudioInput's store subscription;
 * errors (denied / no device) surface inline and auto-toggle the
 * switch back to OFF.
 */
const AudioInputSection: React.FC = () => {
  const supported = useAudioDevicesStore(s => s.supported)
  const inputs = useAudioDevicesStore(s => s.inputs)
  const selectedInputId = useAudioDevicesStore(s => s.selectedInputId)
  const setSelectedInputId = useAudioDevicesStore(s => s.setSelectedInputId)
  const inputBusId = useAudioDevicesStore(s => s.inputBusId)
  const setInputBusId = useAudioDevicesStore(s => s.setInputBusId)
  const monitor = useAudioDevicesStore(s => s.inputMonitorEnabled)
  const setMonitor = useAudioDevicesStore(s => s.setInputMonitorEnabled)
  const buses = useSequencerStore(selectCurrentBuses)
  const { resume } = useAudioEngine()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Subscribe to the AudioInput pipeline's status + errors so we
  // can surface failures to the user. The subscribe call resolves
  // the singleton lazily — if the engine hasn't warmed up yet the
  // effect will short-circuit and re-attach once the ctx exists.
  useEffect(() => {
    const input = getAudioInput()
    if (!input) return
    return input.subscribe({
      onError: msg => setErrorMsg(msg),
      onStatus: s => {
        if (s !== 'error') setErrorMsg(null)
      },
    })
  }, [monitor])

  const captureSupported = canCaptureAudioInput()
  const captureReason = audioInputUnavailableReason()

  const handleToggleMonitor = async () => {
    setErrorMsg(null)
    if (!monitor) {
      // Resume the shared AudioContext on this gesture so the
      // AudioInput singleton has a live context to attach to.
      // Without this, turning monitor on before the user's touched
      // any audio would spin up an output-suspended context and the
      // source node would be silent.
      await resume()
    }
    setMonitor(!monitor)
  }

  return (
    <section className="bg-surface-raised border border-brd-subtle rounded-xl p-3 flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">
          Input device
        </h4>
        <div className="flex items-center gap-2">
          <RecordInputButton />
          <button
            type="button"
            onClick={handleToggleMonitor}
            aria-pressed={monitor}
            disabled={!captureSupported}
            className={`px-2 py-1 rounded border text-[10px] font-mono uppercase tracking-wider transition-colors ${
              monitor
                ? 'border-accent bg-accent text-[color:var(--accent-ink)]'
                : 'border-brd-subtle text-content-secondary hover:border-[color:var(--accent-halo)] hover:text-accent'
            } disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-brd-subtle disabled:hover:text-content-secondary`}
            title={
              !captureSupported
                ? (captureReason ?? 'Input capture unavailable')
                : monitor
                  ? 'Disable input monitoring (silence the incoming stream)'
                  : 'Enable input monitoring — first use will request mic permission'
            }
          >
            Monitor {monitor ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {!supported ? (
        <p className="text-[11px] text-content-tertiary italic">
          Input enumeration isn&apos;t available in this runtime. A fresh app
          build with the microphone entitlement + Info.plist string will unlock
          it.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-[9px] font-mono uppercase tracking-wider text-content-tertiary">
              Source
            </span>
            <select
              value={selectedInputId}
              onChange={e => setSelectedInputId(e.target.value)}
              className="rounded-md border border-brd-subtle bg-surface-sunken px-2 py-1.5 text-[12px] font-mono text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
            >
              <option value={DEFAULT_INPUT_ID}>System default</option>
              {inputs
                .filter(d => d.id && d.id !== DEFAULT_INPUT_ID)
                .map(d => (
                  <option key={d.id} value={d.id}>
                    {formatDeviceOption(d, 'in')}
                  </option>
                ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[9px] font-mono uppercase tracking-wider text-content-tertiary">
              Route to bus
            </span>
            <select
              value={inputBusId}
              onChange={e => setInputBusId(e.target.value)}
              className="rounded-md border border-brd-subtle bg-surface-sunken px-2 py-1.5 text-[12px] font-mono text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
            >
              {buses.map(b => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Live peak meter for the legacy monitor stream. Only
          polls while monitor is on; hidden entirely otherwise so
          an idle panel doesn't run an unused RAF loop. */}
      {monitor && <InputMeter meterId={MONITOR_METER_ID} enabled={monitor} />}

      {errorMsg && (
        <p
          className="text-[10px] font-mono text-destructive bg-destructive/10 border border-destructive/30 rounded px-2 py-1"
          role="alert"
        >
          Input error: {errorMsg}
        </p>
      )}
      {!errorMsg && !captureSupported && captureReason && (
        <p className="text-[10px] text-content-tertiary bg-surface-sunken border border-brd-subtle rounded px-2 py-1">
          {captureReason}
        </p>
      )}
      {!errorMsg && captureSupported && (
        <p className="text-[10px] text-content-tertiary">
          {monitor
            ? 'Monitoring active — the input stream is audible through the chosen bus.'
            : 'Monitor disabled — no audio is routed. Enabling it will prompt for mic permission on first use.'}
        </p>
      )}
    </section>
  )
}

/**
 * Build the `<option>` label for a device row. Surfaces the channel
 * count when the plugin reports one so multi-channel interfaces read
 * as "AF16Rig Audio (16 in)" instead of collapsing to a single line
 * that hides their capacity. Falls back to id-derived labels when
 * the runtime didn't provide a name (common on web before mic
 * permission lands).
 */
function formatDeviceOption(
  d: AudioDeviceInfo,
  direction: 'in' | 'out',
): string {
  const base =
    d.label || `${direction === 'in' ? 'Input' : 'Output'} ${d.id.slice(0, 6)}`
  if (d.channelCount > 0) {
    return `${base} (${d.channelCount} ${direction})`
  }
  return base
}

/**
 * Inline runtime facts for the "device enumeration unavailable"
 * case. Surfaces what we'd otherwise ask the user to dig out via
 * WebInspector: whether mediaDevices is entirely undefined vs.
 * partially implemented, whether the origin counts as a secure
 * context, and the effective UA + origin strings. A small but
 * enormously useful diagnostic when the panel won't populate and
 * we're trying to distinguish between "WebKit API not exposed at
 * all" and "API exposed but permission / origin gating it."
 */
const RuntimeDiagnostics: React.FC = () => {
  const facts = React.useMemo(() => {
    if (typeof window === 'undefined') {
      return [{ label: 'environment', value: 'not browser' }]
    }
    const nav = window.navigator
    const md = nav?.mediaDevices as
      | (MediaDevices & { getUserMedia?: unknown })
      | undefined
    const hasMediaDevices = md != null
    const hasGetUserMedia =
      hasMediaDevices && typeof md.getUserMedia === 'function'
    const hasEnumerate =
      hasMediaDevices && typeof md.enumerateDevices === 'function'
    const ctxCtor =
      typeof AudioContext !== 'undefined'
        ? 'AudioContext'
        : typeof (window as unknown as { webkitAudioContext?: unknown })
              .webkitAudioContext !== 'undefined'
          ? 'webkitAudioContext'
          : 'none'
    return [
      { label: 'isSecureContext', value: String(window.isSecureContext) },
      { label: 'location.origin', value: window.location.origin },
      { label: 'location.protocol', value: window.location.protocol },
      { label: 'navigator.mediaDevices', value: String(hasMediaDevices) },
      { label: 'mediaDevices.getUserMedia', value: String(hasGetUserMedia) },
      { label: 'mediaDevices.enumerateDevices', value: String(hasEnumerate) },
      { label: 'AudioContext', value: ctxCtor },
      {
        label: 'userAgent',
        value: nav?.userAgent?.slice(0, 120) ?? 'unknown',
      },
    ]
  }, [])

  return (
    <details className="mt-2 text-[10px] font-mono text-content-tertiary">
      <summary className="cursor-pointer hover:text-content-secondary">
        Why?
      </summary>
      <table className="mt-1 w-full table-fixed">
        <tbody>
          {facts.map(f => (
            <tr key={f.label}>
              <td className="pr-2 py-0.5 text-content-tertiary align-top whitespace-nowrap">
                {f.label}
              </td>
              <td className="py-0.5 text-content-secondary break-all">
                {f.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  )
}

/**
 * Phase 3d — record the system-default mic to a local sample.
 * Decoupled from the Monitor toggle so users can record without
 * passthrough (avoid feedback) or monitor + record simultaneously.
 * Hidden on runtimes that don't expose `MediaRecorder` +
 * `getUserMedia` (Catalyst today; will flip to the native plugin
 * once that ships).
 */
const RecordInputButton: React.FC = () => {
  const [lastResult, setLastResult] =
    React.useState<AudioRecordingResult | null>(null)
  const [lastResultExpiry, setLastResultExpiry] = React.useState<number | null>(
    null,
  )
  const recording = useAudioRecording({
    onRecordingComplete: result => {
      setLastResult(result)
      // Auto-dismiss the success banner after 6s so it doesn't
      // clutter the panel during multi-take sessions.
      setLastResultExpiry(Date.now() + 6_000)
    },
  })

  useEffect(() => {
    if (!lastResultExpiry) return
    const remaining = lastResultExpiry - Date.now()
    if (remaining <= 0) {
      setLastResult(null)
      setLastResultExpiry(null)
      return
    }
    const t = setTimeout(() => {
      setLastResult(null)
      setLastResultExpiry(null)
    }, remaining)
    return () => clearTimeout(t)
  }, [lastResultExpiry])

  const countInBars = useUIStore(s => s.recordCountInBars)
  const setCountInBars = useUIStore(s => s.setRecordCountInBars)

  const handleClick = () => {
    if (recording.isRecording) {
      void recording.stop().catch(() => {
        /* error already surfaced on the hook */
      })
    } else {
      void recording.start({ countInBars }).catch(() => {
        /* error already surfaced on the hook */
      })
    }
  }

  if (!recording.supported) {
    // Hide the button entirely rather than show it disabled — the
    // Monitor button already fails with the same permission issue
    // on Catalyst and emits a visible error, which is sufficient
    // user feedback. One message beats two.
    return null
  }

  return (
    <div className="flex items-center gap-2">
      {/* Count-in dropdown — sits next to REC so the user can pick
          a pre-roll length in the same click path. Setting is
          persisted in uiStore and applies to every record-to-pad
          flow too (useRecordToPad reads from uiStore on start). */}
      <select
        value={countInBars}
        onChange={e => setCountInBars(Number(e.target.value) as CountInBars)}
        disabled={recording.isRecording}
        title="Count-in bars — play N bars of click-track at the project BPM before recording actually starts."
        className="text-[10px] font-mono rounded border border-brd-subtle bg-surface-sunken px-1 py-1 text-content-secondary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)] disabled:opacity-50"
      >
        <option value={0}>No count-in</option>
        <option value={1}>1 bar count-in</option>
        <option value={2}>2 bars count-in</option>
        <option value={4}>4 bars count-in</option>
      </select>
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={recording.isRecording}
        className={`px-2 py-1 rounded border text-[10px] font-mono uppercase tracking-wider transition-colors ${
          recording.countingIn
            ? 'border-accent bg-accent/20 text-accent animate-pulse'
            : recording.isRecording
              ? 'border-destructive bg-destructive text-white animate-pulse'
              : 'border-brd-subtle text-content-secondary hover:border-destructive hover:text-destructive'
        }`}
        title={
          recording.countingIn
            ? `Count-in running — capture starts in ${recording.countInRemainingSec.toFixed(1)}s.`
            : recording.isRecording
              ? `Recording — ${recording.elapsedSec.toFixed(1)}s. Click to stop.`
              : countInBars > 0
                ? `Record the system-default mic to a new local sample (after a ${countInBars}-bar count-in).`
                : 'Record the system-default mic to a new local sample.'
        }
      >
        {recording.countingIn
          ? `CNT ${recording.countInRemainingSec.toFixed(1)}s`
          : recording.isRecording
            ? `REC ${recording.elapsedSec.toFixed(1)}s`
            : 'REC'}
      </button>
      {recording.error && (
        <span
          className="text-[10px] font-mono text-destructive"
          role="alert"
          title={recording.error}
        >
          err
        </span>
      )}
      {lastResult && !recording.isRecording && (
        <span
          className="text-[10px] font-mono text-accent"
          title={`Saved as ${lastResult.name} (${lastResult.durationSec.toFixed(1)}s)`}
        >
          ✓ {lastResult.name}
        </span>
      )}
    </div>
  )
}
